import React, { useCallback, useEffect, useState } from 'react';
import {
  AppBar, Toolbar, Box, Typography, Button, IconButton, Table, TableHead, TableBody,
  TableRow, TableCell, Chip, Alert, CircularProgress, Paper,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import Wordmark from '../components/Wordmark';
import RaceFormDialog from './RaceFormDialog';
import type { AdminRace } from './types';

const apiUrl = process.env.REACT_APP_API_URL;

const statusColor: Record<string, 'success' | 'info' | 'default'> = {
  Live: 'success',
  Upcoming: 'info',
  Complete: 'default',
};

const AdminDashboard = ({ onLoggedOut }: { onLoggedOut: () => void }) => {
  const [races, setRaces] = useState<AdminRace[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRace, setEditingRace] = useState<AdminRace | null>(null);

  const loadRaces = useCallback(() => {
    setLoading(true);
    fetch(`${apiUrl}/api/admin/races`, { credentials: 'include' })
      .then(async (res) => {
        if (!res.ok) throw new Error('Failed to load races');
        const body = await res.json();
        setRaces(body.races.sort((a: AdminRace, b: AdminRace) => new Date(b.date).getTime() - new Date(a.date).getTime()));
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadRaces(); }, [loadRaces]);

  const handleLogout = () => {
    fetch(`${apiUrl}/api/admin/logout`, { method: 'POST', credentials: 'include' }).finally(onLoggedOut);
  };

  const handleAdd = () => { setEditingRace(null); setDialogOpen(true); };
  const handleEdit = (race: AdminRace) => { setEditingRace(race); setDialogOpen(true); };

  const handleDelete = (race: AdminRace) => {
    if (!window.confirm(`Delete "${race.name}"? This removes its folder (info, logo, results) permanently.`)) return;
    fetch(`${apiUrl}/api/admin/races/${encodeURIComponent(race.directory)}`, { method: 'DELETE', credentials: 'include' })
      .then(async (res) => {
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.error || 'Delete failed');
        }
        loadRaces();
      })
      .catch((err) => setError(err.message));
  };

  const handleSaved = () => {
    setDialogOpen(false);
    loadRaces();
  };

  // Duplicates with the server's defaults (name gets "(Copy)", status resets
  // to Upcoming, results.txt is not carried over — see server.js) and opens
  // the new copy straight into Edit so the date/name can be adjusted.
  const handleDuplicate = (race: AdminRace) => {
    fetch(`${apiUrl}/api/admin/races/${encodeURIComponent(race.directory)}/duplicate`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: '{}',
    })
      .then(async (res) => {
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.error || 'Duplicate failed');
        }
        const body = await res.json();
        loadRaces();
        setEditingRace(body.race);
        setDialogOpen(true);
      })
      .catch((err) => setError(err.message));
  };

  return (
    <Box>
      <AppBar position="static">
        <Toolbar>
          <Box sx={{ flexGrow: 1 }}>
            <Wordmark variant="h6" component="span" />
            <Typography component="span" sx={{ marginLeft: 1 }} variant="h6">— Admin</Typography>
          </Box>
          <Button color="inherit" onClick={handleLogout}>Log out</Button>
        </Toolbar>
      </AppBar>

      <Box sx={{ padding: 2 }}>
        {error && <Alert severity="error" sx={{ marginBottom: 2 }} onClose={() => setError(null)}>{error}</Alert>}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 2 }}>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleAdd}>Add race</Button>
        </Box>

        <Paper>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', padding: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Results</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {races.map((race) => (
                  <TableRow key={race.directory}>
                    <TableCell>{race.name}</TableCell>
                    <TableCell>{race.date}</TableCell>
                    <TableCell>
                      <Chip size="small" label={race.status} color={statusColor[race.status] || 'default'} />
                    </TableCell>
                    <TableCell>{race.isExternal === 'true' ? 'External' : 'Internal (results.txt)'}</TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => handleEdit(race)} aria-label="Edit">
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDuplicate(race)} aria-label="Duplicate">
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDelete(race)} aria-label="Delete">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {races.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <Typography variant="body2" color="text.secondary" sx={{ padding: 2, textAlign: 'center' }}>
                        No races yet — add one to get started.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </Paper>
      </Box>

      <RaceFormDialog
        open={dialogOpen}
        mode={editingRace ? 'edit' : 'create'}
        initialRace={editingRace}
        onClose={() => setDialogOpen(false)}
        onSaved={handleSaved}
      />
    </Box>
  );
};

export default AdminDashboard;
